import { DataValue, DataArray, ScaleConfig, DatasetConfig } from '../types';

/**
 * Get the numeric value from a data point
 */
export const getNumericValue = (value: DataValue): number => {
  if (Array.isArray(value)) {
    return value[1]; // For floating bars, use the end value for max calculations
  }
  return value;
};

/**
 * Get the minimum value from a data point (for floating bars)
 */
export const getMinValue = (value: DataValue): number => {
  if (Array.isArray(value)) {
    return value[0];
  }
  return 0;
};

/**
 * Get the maximum value from a data point
 */
export const getMaxValue = (value: DataValue): number => {
  if (Array.isArray(value)) {
    return Math.max(value[0], value[1]);
  }
  return value;
};

/**
 * Calculate nice tick values for an axis
 */
export const calculateNiceTicks = (
  min: number,
  max: number,
  maxTicks: number = 10
): number[] => {
  const range = max - min;
  if (range === 0) return [min];

  // Calculate a nice step size
  const roughStep = range / (maxTicks - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / magnitude;

  let niceStep: number;
  if (normalized <= 1) niceStep = magnitude;
  else if (normalized <= 2) niceStep = 2 * magnitude;
  else if (normalized <= 5) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;

  // Calculate nice min and max
  const niceMin = Math.floor(min / niceStep) * niceStep;
  const niceMax = Math.ceil(max / niceStep) * niceStep;

  // Generate ticks
  const ticks: number[] = [];
  for (let tick = niceMin; tick <= niceMax + niceStep / 2; tick += niceStep) {
    ticks.push(Math.round(tick * 1000000) / 1000000); // Fix floating point
  }

  return ticks;
};

/**
 * Calculate scale configuration from datasets
 */
export const calculateScale = (
  datasets: DatasetConfig[],
  options: {
    min?: number;
    max?: number;
    beginAtZero?: boolean;
    maxTicks?: number;
    stacked?: boolean;
  } = {}
): ScaleConfig => {
  const { min: fixedMin, max: fixedMax, beginAtZero = true, maxTicks = 8, stacked = false } = options;

  let dataMin = Infinity;
  let dataMax = -Infinity;

  if (stacked) {
    // For stacked charts, we need to sum values at each index
    const numPoints = datasets[0]?.data.length || 0;
    
    for (let i = 0; i < numPoints; i++) {
      let positiveSum = 0;
      let negativeSum = 0;
      
      for (const dataset of datasets) {
        if (dataset.hidden) continue;
        const value = getNumericValue(dataset.data[i]);
        if (value >= 0) {
          positiveSum += value;
        } else {
          negativeSum += value;
        }
      }
      
      dataMin = Math.min(dataMin, negativeSum);
      dataMax = Math.max(dataMax, positiveSum);
    }
  } else {
    // For regular charts, find min/max across all data
    for (const dataset of datasets) {
      if (dataset.hidden) continue;
      for (const value of dataset.data) {
        const numVal = getNumericValue(value);
        const minVal = getMinValue(value);
        dataMin = Math.min(dataMin, minVal, numVal);
        dataMax = Math.max(dataMax, getMaxValue(value));
      }
    }
  }

  // Handle edge cases
  if (dataMin === Infinity) dataMin = 0;
  if (dataMax === -Infinity) dataMax = 100;
  if (dataMin === dataMax) {
    dataMin = dataMin - 1;
    dataMax = dataMax + 1;
  }

  // Apply beginAtZero
  if (beginAtZero) {
    if (dataMin > 0) dataMin = 0;
    if (dataMax < 0) dataMax = 0;
  }

  // Apply fixed min/max if provided
  const min = fixedMin ?? dataMin;
  const max = fixedMax ?? dataMax;

  // Calculate ticks
  const ticks = calculateNiceTicks(min, max, maxTicks);

  return {
    min: ticks[0],
    max: ticks[ticks.length - 1],
    step: ticks.length > 1 ? ticks[1] - ticks[0] : 1,
    ticks,
  };
};

/**
 * Convert a value to a position on the chart
 */
export const valueToPosition = (
  value: number,
  scale: ScaleConfig,
  size: number,
  inverted: boolean = false
): number => {
  const { min, max } = scale;
  const range = max - min;
  if (range === 0) return 0;
  
  const normalized = (value - min) / range;
  const position = normalized * size;
  
  return inverted ? size - position : position;
};

/**
 * Convert a position to a value
 */
export const positionToValue = (
  position: number,
  scale: ScaleConfig,
  size: number,
  inverted: boolean = false
): number => {
  const { min, max } = scale;
  const range = max - min;
  
  const normalized = inverted ? (size - position) / size : position / size;
  return min + normalized * range;
};

/**
 * Format a number for display
 */
export const formatValue = (
  value: number,
  options: {
    decimals?: number;
    prefix?: string;
    suffix?: string;
    thousandsSeparator?: string;
  } = {}
): string => {
  const { decimals = 0, prefix = '', suffix = '', thousandsSeparator = ',' } = options;
  
  let formatted = value.toFixed(decimals);
  
  // Add thousands separator
  if (thousandsSeparator) {
    const parts = formatted.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    formatted = parts.join('.');
  }
  
  return `${prefix}${formatted}${suffix}`;
};

